import {
  findingTypeFromCodes,
  qrIsNormalChip,
  qrIsPosNegChip,
  type Questionnaire,
  type QuestionnaireResponse,
  type QItem,
} from '@scripsteam/scrips-signal-ds';
import { ENCOUNTER_NARRATIVE_LABEL, ENCOUNTER_NARRATIVE_LINK_ID } from './ambient/orphan-sink';
import {
  QUESTIONNAIRE_STEPS,
  hasStepSectioning,
  linkIdInSection,
  type QuestionnaireStep,
} from './lib/questionnaire-groups';

export interface NoteSection {
  section: string;
  lines: string[];
  /**
   * The authored categories that fed this section, each with its own lines (PROD-2011).
   *
   * Flutter's note component renders a TABLE beneath the tab heading whose rows are the
   * categories (`notes_component_widget.dart:590` renders `e.title`), not one flat run of text.
   * Populated by `groupNoteByStep`; absent on the raw per-group output, where the section IS the
   * category.
   */
  categories?: { title: string; lines: string[] }[];
  /** Source identity — the top-level Questionnaire item this section derives from. */
  linkId?: string;
  /**
   * The encounter step this section belongs to (PROD-1588 interaction fix I3 — the
   * generated-note "Go To <section>" deep link's target). Resolved by the SAME
   * section-membership convention the step router uses (`linkIdInSection` over the
   * step-prefixed linkIds); an unsectioned template renders wholly on HPI, so every
   * section maps there. `undefined` when the section belongs to no step (it never
   * renders on the canvas) — the host must then render NO link (unknown = no-op).
   */
  step?: QuestionnaireStep;
  /**
   * Titles of the groups ABOVE this section, outermost first -- so a renderer can show
   * `Subjective > History of Present Illness` (PROD-2005).
   *
   * **Absent, not empty, at the top level.** A two-level template's output has to stay
   * byte-identical to what it was before nesting was supported, and `ancestors: []` would be a
   * new key on every section production renders today.
   */
  ancestors?: string[];
  /**
   * linkIds in this section whose answer was written by the model, not the clinician.
   *
   * Structure as well as the visible marker in `lines`, so a future DS cue can key off data rather
   * than parse a label. Absent when nothing in the section is AI-authored — an empty array would be
   * a new key on every section production renders today.
   */
  aiLinkIds?: string[];
}

/**
 * The visible provenance marker, appended to an AI-authored line's LABEL.
 *
 * DS-021 decorates the QuestionnaireRenderer, and the generated note is a different surface that
 * never saw the flag — so model-drafted clinical prose rendered in the note indistinguishably from
 * the clinician's own words, in the surface they read and sign. Found by cross-model review after
 * the change that introduced it claimed provenance "needed nothing built".
 *
 * In the LABEL rather than the value, so the marker cannot be mistaken for part of the clinical
 * text, and in text rather than styling, because the note is copied, printed and read outside this
 * app. The note is a live mirror of the response, not the stored record, so a display marker costs
 * nothing and reaches every consumer.
 */
export const AI_DRAFTED_MARKER = ' (AI-drafted)';

/** The encounter step a top-level section linkId belongs to — `undefined` when none matches. */
function stepForSection(linkId: string, sectioned: boolean): QuestionnaireStep | undefined {
  if (!sectioned) return 'hpi'; // unsectioned template: HPI owns the whole questionnaire
  return QUESTIONNAIRE_STEPS.find((s) => linkIdInSection(linkId, s));
}

/** Resolve a stored answer code to its option display label (falls back to the
 *  raw code when the item has no matching option — e.g. text/number answers). */
function optionLabel(item: QItem | undefined, code: string): string {
  const opt = item?.answerOption?.find((o) => (o.valueCoding?.code ?? o.valueString) === code);
  return opt?.valueCoding?.display ?? opt?.valueString ?? code;
}

/** A number as the text a numeric field stored: signed, decimal (".5", "5."), or exponent ("1e3"). */
const NUMERIC_TEXT = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;

/**
 * The item's unit after a numeric answer (PROD-2786, contract UC-10).
 *
 * `QItem.unit` is the SDC `questionnaire-unit` code the FHIR mapper surfaces and the renderer draws
 * beside the field, so the clinician typed "7.2" next to "%". The note printed the bare "7.2": an
 * HbA1c without its unit, a glucose that could be mg/dL or mmol/L. Only a NUMBER gets the unit --
 * a coded option or free text already says what it says.
 */
function withUnit(text: string, value: unknown, item?: QItem): string {
  const unit = item?.unit?.trim();
  if (!unit) return text;
  const numeric =
    typeof value === 'number'
      ? Number.isFinite(value)
      : typeof value === 'string' && NUMERIC_TEXT.test(value.trim()) && Number.isFinite(Number(value));
  return numeric ? `${text} ${unit}` : text;
}

/** Format a raw answer value into a human-readable string, mapping coded
 *  choice values to their option displays. `legacy` is the formatter as it was before PROD-2786
 *  (no unit), kept for a note signed under it -- see `NoteOptions.legacyWording`. */
function formatValue(value: unknown, item?: QItem, legacy = false): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleDateString();
  if (Array.isArray(value)) return value.map((v) => optionLabel(item, String(v))).join(', ');
  if (legacy) return optionLabel(item, String(value));
  const raw = String(value);
  const isOption = item?.answerOption?.some((o) => (o.valueCoding?.code ?? o.valueString) === raw) ?? false;
  return isOption ? optionLabel(item, raw) : withUnit(raw, value, item);
}

/**
 * How a finding is stated in the note (PROD-2786; contract UC-07, UC-08, UC-10; spec §4.5).
 *
 * The note is the legal record, and it printed findings as "Chest pain: No" and
 * "Edema present: Yes". A clinician writes "Denies chest pain" and "Edema present". The wording is
 * decided by what the TEMPLATE carries, never guessed from the text:
 *
 *  - The item must be a finding: a `boolean` with itemControl `symptom-chip` (the DS's own
 *    `qrIsPosNegChip` / `qrIsNormalChip`, so the note and the chip agree on what a chip is).
 *  - History vs exam comes from the step the item sits in: ROS is history, PE is exam. Nothing else
 *    is classified. HPI chips in every template measured are descriptors (Location "Lower back",
 *    Severity "Moderate"), and "Denies Lower back" would misstate them. An unsectioned template
 *    has no step to read at all.
 *  - History, tri-state: true → "Reports <finding>", false → "Denies <finding>".
 *  - Exam, tri-state ("+"-type, abnormal): true → "<finding> present", or the finding as written
 *    when its text already says "present" / "positive"; false → "No <finding>". Only when the
 *    chip's OWN system (its group) codes `cps-finding-type` on at least one item: a system authored
 *    before that carrier existed could hold an uncoded normal statement ("Well-Developed"), and
 *    "No Well-Developed" would state it wrongly. Per system, not per template, so one coded item
 *    elsewhere cannot reclassify an older system's chips.
 *  - Normal-type (two-state) chip, either step: true → the statement exactly as written.
 *  - Unset → nothing is printed (the caller drops empty answers), so "not asked" never reaches the
 *    note (AC-07.1).
 *
 * Anything else returns `undefined` and prints exactly as it did before ("Label: Yes"): a plain
 * yes/no question, a value that is not a boolean (an old record's "pos" / "neg" / "yes" prints as
 * stored, spec §4.5), a normal-type chip stored `false` (the chip cannot write that, so the record
 * predates its coding), and any "+"-type finding whose text the wording would garble
 * (`WORDING_UNSAFE_TEXT`: "No tenderness" would become "Denies No tenderness", "Breath sounds
 * absent" would become "Breath sounds absent present").
 */
type FindingKind = 'history' | 'exam';

/**
 * Finding text a "Reports / Denies / present / No" wording would garble, ANYWHERE in the text: a
 * negation ("No fever", "Non-tender", "Breath sounds absent", "Murmur not present"), a statement of
 * normality ("Heart sounds normal", "Mood appropriate" -- a normal statement left "+"-type would
 * read "No Heart sounds normal"), an existing report, or a question. Such an item prints as stored
 * rather than being reworded into a double negative or a contradiction.
 */
const WORDING_UNSAFE_TEXT =
  /\b(?:no|non|not|nor|never|none|nil|without|absent|absence|negative|neg|free|denies|denied|deny|reports|reported|normal|appropriate|intact|unremarkable|wnl)\b|n't\b|\?\s*$/i;
/** Finding text that already states presence ("Edema present", "Guaiac positive"). */
const STATES_PRESENCE = /\b(?:present|positive)\b/i;

function findingKindForStep(step: QuestionnaireStep | undefined, sectioned: boolean): FindingKind | undefined {
  if (!sectioned) return undefined;
  if (step === 'ros') return 'history';
  if (step === 'pe') return 'exam';
  return undefined;
}

/** Does any of these sibling items carry a finding type? (None in a system = that system predates
 *  the `cps-finding-type` carrier, so an uncoded exam chip in it is of unknown flavour.) */
function codesFindingType(siblings: readonly QItem[]): boolean {
  return siblings.some((it) => it.findingType != null || findingTypeFromCodes(it) != null);
}

function findingStatement(
  item: QItem,
  value: unknown,
  kind: FindingKind | undefined,
  systemCodesFindingType: boolean,
): string | undefined {
  if (kind === undefined || typeof value !== 'boolean') return undefined;
  const text = item.text?.trim();
  if (!text) return undefined;
  if (qrIsNormalChip(item)) return value ? text : undefined;
  if (!qrIsPosNegChip(item) || WORDING_UNSAFE_TEXT.test(text)) return undefined;
  if (kind === 'history') return value ? `Reports ${text}` : `Denies ${text}`;
  if (!systemCodesFindingType) return undefined;
  if (value) return STATES_PRESENCE.test(text) ? text : `${text} present`;
  return `No ${text}`;
}

/** Flatten all items in a questionnaire into a linkId → QItem index. */
function buildItemIndex(items: QItem[]): Map<string, QItem> {
  const map = new Map<string, QItem>();
  function walk(list: QItem[]) {
    for (const item of list) {
      map.set(item.linkId, item);
      if (item.item) walk(item.item);
    }
  }
  walk(items);
  return map;
}

/**
 * Derive a readable note from a QuestionnaireResponse.
 *
 * Groups become note sections at ANY depth (PROD-2005); a group's answered leaf items become its
 * lines, and nested groups follow it in template order carrying an `ancestors` chain. A group with
 * no answered leaves of its own emits nothing.
 * Unanswered items are omitted.
 *
 * @param questionnaire - The FHIR Questionnaire template
 * @param response - The captured QuestionnaireResponse
 * @returns Array of { section, lines } — each section maps to a group title
 */
/**
 * `NoteSection[]` → the DS `GeneratedNote` section shape (PROD-1988).
 *
 * Extracted from EncounterDrawer, which had it inline, so the pre-encounter rail's Note tab
 * renders the note through the SAME transform instead of a second copy of it. `onGoToStep` is
 * optional: hosts that can navigate steps pass it and get the per-section "Go To" deep link;
 * hosts that cannot (the pre-encounter rail — there are no steps before Start) omit it and get no
 * dead affordance.
 */
export function toGeneratedNoteSections(
  sections: NoteSection[] | undefined,
  onGoToStep?: (step: QuestionnaireStep) => void,
): {
  heading: string;
  body?: string;
  fields?: { label: string; value: string }[];
  onGoTo?: () => void;
}[] {
  return (sections ?? []).map((s) => {
    const step = s.step;
    // Categories → the DS `fields` label/value rows, which is Flutter's table beneath the tab
    // heading (`notes_component_widget.dart:590` renders each category's title as the row key).
    // A section with no categories — the raw per-group shape, or a stepless one — keeps the flat
    // body it always had.
    const categories = (s.categories ?? []).filter((c) => c.lines.length > 0);
    const isEmpty = s.lines.length === 0;
    const emptyLine = isEmpty && step ? STEP_NOTE_EMPTY[step] : undefined;
    return {
      heading: s.section,
      // An empty step shows Flutter's own "nothing yet" line rather than a blank section
      // (`notes_component_widget.dart:124-127`).
      body: isEmpty ? emptyLine : categories.length > 0 ? undefined : s.lines.join('\n'),
      fields:
        categories.length > 0
          ? categories.map((c) => ({ label: c.title, value: c.lines.join('\n') }))
          : undefined,
      onGoTo: onGoToStep && step ? () => onGoToStep(step) : undefined,
    };
  });
}

/**
 * The encounter narrative sink, emitted as a trailing note section.
 *
 * `responseToNote` walks the TEMPLATE and looks each item's answer up — so an answer whose linkId is
 * not in the template is invisible to it. That is correct for stray data and wrong for the one
 * reserved linkId Contract E guarantees, which exists precisely BECAUSE the template has no item
 * for it.
 *
 * Emitted last, under its own heading, and only when it holds something. A clinician's added notes
 * and dictated orphans share it, so the section is not marked AI-authored as a whole — provenance
 * is per line, in the text.
 */
function appendSinkSection(sections: NoteSection[], response: QuestionnaireResponse): void {
  const sink = response.answers.find((a) => a.linkId === ENCOUNTER_NARRATIVE_LINK_ID);
  const value = typeof sink?.value === 'string' ? sink.value.trim() : '';
  if (!value) return;
  sections.push({
    section: ENCOUNTER_NARRATIVE_LABEL,
    lines: value.split('\n').map((l) => l.trim()).filter(Boolean),
    linkId: ENCOUNTER_NARRATIVE_LINK_ID,
  });
}

export interface NoteOptions {
  /**
   * Print with the formatter as it was before PROD-2786 -- "Fever: No", "HbA1c: 7.2" -- byte for
   * byte, never "Denies Fever" / "HbA1c: 7.2 %".
   *
   * The page sets it from `signedUnderLegacyNoteWording` (lib/to-sign-lock.ts): a PROVIDER-signed
   * note whose sign time is before `NOTE_WORDING_CUTOFF_UTC`, or whose sign time is missing. The
   * note is not stored; it is re-derived from the answers on every view, and a signed note must not
   * change on view (contract G1 / G17, PROD-2810). A note signed at or after the cutoff was reviewed
   * in the new wording and keeps it, so signing never flips the wording.
   *
   * TEMPORARY. Removed once PROD-2810 freezes the narrative at sign: a signed note then renders from
   * its frozen text and this formatter is never asked to re-word it.
   */
  legacyWording?: boolean;
}

export function responseToNote(
  questionnaire: Questionnaire,
  response: QuestionnaireResponse,
  options: NoteOptions = {},
): NoteSection[] {
  // Signed under the old wording: the pre-PROD-2786 formatter, untouched (NoteOptions.legacyWording).
  const legacy = options.legacyWording === true;
  const answerMap = new Map(response.answers.map((a) => [a.linkId, a.value]));
  // Which answers the model wrote. Read alongside the values so a line can be labelled at the
  // point it is built.
  const aiAuthored = new Set(response.answers.filter((a) => a.aiAuthored).map((a) => a.linkId));
  const itemIndex = buildItemIndex(questionnaire.item);
  // Section → step identity (I3 deep links): resolved once per note off the template shape.
  const sectioned = hasStepSectioning(questionnaire.item);
  /** One answered leaf's note line, or '' when it has no answer. A classified finding is stated
   *  as a clinician writes it; everything else keeps "Label: value". `siblings` are the leaf's own
   *  group's items -- whether they code a finding type decides if an uncoded exam chip is a known
   *  "+"-type finding (findingStatement). */
  function leafLine(
    item: QItem,
    siblings: readonly QItem[],
    step: QuestionnaireStep | undefined,
    withLabel: boolean,
  ): string {
    const value = answerMap.get(item.linkId);
    const formatted = formatValue(value, item, legacy);
    if (formatted === '') return '';
    const marker = aiAuthored.has(item.linkId) ? AI_DRAFTED_MARKER : '';
    const kind = legacy ? undefined : findingKindForStep(step, sectioned);
    const statement = findingStatement(item, value, kind, codesFindingType(siblings));
    if (statement !== undefined) return `${statement}${marker}`;
    if (!withLabel) return formatted;
    return `${item.text ?? item.linkId}${marker}: ${formatted}`;
  }

  const sections: NoteSection[] = [];

  /**
   * Emit `group` and everything nested under it, in template order (PROD-2005).
   *
   * A group contributes ONE section built from its direct answered leaves, then its subgroups
   * follow. Collecting the leaves rather than emitting them where they sit means a group with
   * fields either side of a subgroup does not appear twice under the same heading -- a duplicate
   * heading is worse than either ordering of a single one.
   *
   * `step` is resolved once from the TOP-LEVEL ancestor and passed down, because step membership
   * is a convention over step-prefixed top-level linkIds (`linkIdInSection`). A nested group's
   * own linkId may not carry the prefix, so re-resolving per level would silently drop the deep
   * link on exactly the templates this fix exists to support.
   */
  function emitGroup(group: QItem, ancestors: string[], step: QuestionnaireStep | undefined) {
    const lines: string[] = [];
    const aiLinkIds: string[] = [];
    for (const child of group.item ?? []) {
      // Skip subgroups here. Calling formatValue on a group linkId is what produced the empty
      // note: a group has no answer, so every line was '' and the section was dropped.
      if (child.type === 'group') continue;
      const line = leafLine(child, group.item ?? [], step, true);
      if (line !== '') {
        if (aiAuthored.has(child.linkId)) aiLinkIds.push(child.linkId);
        lines.push(line);
      }
    }

    // A group with no answered leaves of its own contributes context, not a heading. Ancestors
    // like `Subjective` reach the renderer through the `ancestors` chain below instead.
    if (lines.length > 0) {
      sections.push({
        section: group.text ?? group.linkId,
        lines,
        linkId: group.linkId,
        step,
        ...(ancestors.length > 0 ? { ancestors } : {}),
        ...(aiLinkIds.length > 0 ? { aiLinkIds } : {}),
      });
    }

    for (const child of group.item ?? []) {
      if (child.type === 'group') {
        emitGroup(child, [...ancestors, group.text ?? group.linkId], step);
      }
    }
  }

  for (const top of questionnaire.item) {
    const identity = { linkId: top.linkId, step: stepForSection(top.linkId, sectioned) };
    if (top.type === 'group') {
      emitGroup(top, [], identity.step);
    } else {
      // Flat template (e.g. the real GetEncounterTemplate): each top-level item
      // IS a section; its selected value(s) are the line (mapped to displays).
      const line = leafLine(itemIndex.get(top.linkId) ?? top, questionnaire.item, identity.step, false);
      if (line !== '') {
        sections.push({ section: top.text ?? top.linkId, lines: [line], ...identity });
      }
    }
  }

  // Contract E: the guaranteed landing zone, last, and only when it holds something.
  appendSinkSection(sections, response);

  return sections;
}

/**
 * The note's section headings, per step — Flutter's fixed per-tab titles, NOT authored content.
 *
 * Source: `ui_data.dart:783-846` (`chiefComplaintUiData`, `riskAssessmentUiData`, `rosUiData`,
 * `peUiData`), rendered as the section header by `notes_component_widget.dart:169`
 * (`uiData.title.toUpperCase()`). Note that HPI's heading is "Chief Complaint", not "HPI" —
 * Flutter's own naming, kept rather than tidied.
 */
/**
 * The "nothing here yet" line per step — Flutter's `description` on the same uiData objects
 * (`ui_data.dart:783-846`), rendered when a section has no data
 * (`notes_component_widget.dart:124-127`). Flutter's wording, including HPI's "No CC data
 * selected." which does not match the pattern of the others.
 */
export const STEP_NOTE_EMPTY: Record<QuestionnaireStep, string> = {
  hpi: 'No CC data selected.',
  risk: 'No Risk Assessment yet.',
  ros: 'No ROS yet.',
  pe: 'No Physical Exam yet.',
};

/** A/P is not a questionnaire step; its heading and empty line come from `apUiData` (`:838`). */
export const AP_NOTE_TITLE = 'Assessment & Plan';
export const AP_NOTE_EMPTY = 'No Assessment & Plan yet.';

/**
 * Which steps this template actually authors — the React stand-in for Flutter's tenant feature
 * flags (`isRosFeatureEnable` / `isPeFeatureEnable` / `isPainAssessment`), which have no
 * equivalent here. A template with no ROS groups is this system's way of saying the tenant has
 * no ROS, so the note shows no ROS section at all rather than an empty one.
 */
export function stepsInQuestionnaire(questionnaire: Questionnaire): QuestionnaireStep[] {
  const sectioned = hasStepSectioning(questionnaire.item);
  const present = new Set<QuestionnaireStep>();
  for (const top of questionnaire.item) {
    const step = stepForSection(top.linkId, sectioned);
    if (step) present.add(step);
  }
  return QUESTIONNAIRE_STEPS.filter((s) => present.has(s));
}

export const STEP_NOTE_TITLES: Record<QuestionnaireStep, string> = {
  hpi: 'Chief Complaint',
  risk: 'Risk Assessment',
  ros: 'Review of Systems',
  pe: 'Physical Exam',
};

/**
 * Fold `responseToNote`'s per-authored-group sections into ONE section per step, headed by the
 * step's Flutter title (PROD-2011).
 *
 * Why this is a separate pass rather than a change to `responseToNote`: that function's per-group
 * output carries the section→step identity the "Go To" deep links resolve against, and is pinned
 * by a lot of tests. Grouping on top keeps both shapes available and testable.
 *
 * Order follows Flutter's list — HPI · Risk · ROS · PE (`generated_notes_widget.dart:183-191`) —
 * not the order the groups happen to appear in the template, so the note reads the same way
 * whatever the author did. A section whose step did not resolve keeps its own heading and its
 * template position: it belongs to no step, so there is no tab name to give it.
 */
export function groupNoteByStep(
  sections: NoteSection[],
  /**
   * Which steps get a section. Defaults to ALL of them — Andrew, 2026-08-21: "you should show all
   * tabs sections even if they are empty in the notes." So an unanswered step still appears,
   * carrying its Flutter "No X yet." line, exactly as Flutter renders every note component and
   * lets each show its `description` when it has no data
   * (`notes_component_widget.dart:124-127`).
   *
   * This REPLACES an earlier reading of mine, where only steps the template authored appeared —
   * I had used "does the template author this step" as a stand-in for Flutter's tenant feature
   * flags (`isRosFeatureEnable` etc., which have no React equivalent). Andrew's instruction
   * settles it: the note lists the tabs, not the template's contents.
   */
  stepsPresent: QuestionnaireStep[] = [...QUESTIONNAIRE_STEPS],
): NoteSection[] {
  const grouped: NoteSection[] = [];
  for (const step of QUESTIONNAIRE_STEPS) {
    const members = sections.filter((s) => s.step === step);
    if (members.length === 0 && !stepsPresent.includes(step)) continue;
    grouped.push({
      section: STEP_NOTE_TITLES[step],
      lines: members.flatMap((m) => m.lines),
      // Flutter's table rows: one per authored category that actually has answers.
      categories: members.map((m) => ({ title: m.section, lines: m.lines })),
      step,
      // Identity = the first contributing group, so anything keying off linkId still has one.
      linkId: members[0]?.linkId,
    });
  }
  // Stepless sections (a template shape the step router does not recognise) pass through
  // unchanged rather than being dropped — losing a clinician's answers to tidy a heading would
  // be the worse bug.
  for (const s of sections) {
    if (!s.step) grouped.push(s);
  }
  return grouped;
}
